import React, { useEffect, useRef, useState } from 'react';
import { useWindowWidth } from '@react-hook/window-size';
import { useFocusSource } from '@xp-utilities/web';
import SliderControls from './SliderControls';
import SlideList from './SlideList';
import { setLimitsOnOffset } from './slideTrackReducer';
import { sliderPropTypes } from './props';
import useSlider from './useSlider';
import { DEFAULT_DRAG_THRESHOLD } from './constants';

export default function Slider({
  label,
  previousLabel = undefined,
  minimumSlideWidth = undefined,
  nextLabel = undefined,
  onSlideChange = undefined,
  onFocus = undefined,
  slides,
  slidesToShow = undefined,
  sliderControlOffset = undefined,
  isInfinite = false,
  showControls = true,
  description = 'carousel',
  currentIndex = undefined,
}) {
  const focusSource = useFocusSource();
  const windowWidth = useWindowWidth();
  const [containerWidth, setContainerWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragDelta, setDragDelta] = useState(0);
  const dragOrigin = useRef(0);
  const isClickBlocked = useRef(false);
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const slideRefs = useRef([]);
  const isExternalUpdate = useRef(false);
  const prevCurrentIndex = useRef(currentIndex);
  const prevStateIndex = useRef(state?.index ?? 0);

  const {
    state,
    isScrollAllowed,
    handleOnSlideLeft,
    handleOnSlideRight,
    handleOnSlideSelect,
    handleDetermineNextSlideAfterDrag,
  } = useSlider(
    slides,
    {
      containerRef,
      trackRef,
      containerWidth,
      isInfinite,
      slidesToShow,
    },
  );

  const onDragStart = (x) => {
    dragOrigin.current = x;
    setIsDragging(true);
  };

  const onDragMove = (x) => {
    if (!isDragging) return;

    const delta = x - dragOrigin.current;
    if (Math.abs(delta) < DEFAULT_DRAG_THRESHOLD) return;
    setDragDelta(delta);
  };

  const onDragReset = () => {
    dragOrigin.current = 0;
    setDragDelta(0);
  };

  const handleOnMouseDown = (event) => {
    if (event.button > 0) return;
    onDragStart(event.clientX);
  };

  const handleOnMouseMove = (event) => {
    onDragMove(event.clientX);
  };

  const handleOnMouseUp = () => {
    setIsDragging(false);
    if (dragDelta === 0) return;
    handleDetermineNextSlideAfterDrag(dragDelta, false);

    isClickBlocked.current = true;
    setTimeout(() => { isClickBlocked.current = false; }, 0);
    onDragReset();
  };

  const handleOnMouseLeave = () => {
    setIsDragging(false);
    handleDetermineNextSlideAfterDrag(dragDelta, false);
    onDragReset();
  };

  const handleOnTouchStart = (event) => {
    const isAtFirstSlide = state.index === 0;
    const isAtLastSlide = state.index >= state.slides.length - 1;

    if (isAtFirstSlide || isAtLastSlide) {
      event.preventDefault();
    }

    const { clientX } = event.touches[0];
    dragOrigin.current = clientX;

    if (!isInfinite && isAtLastSlide) {
      setIsDragging(true);
    } else {
      setIsDragging(true);
    }
  };

  const handleOnTouchMove = (event) => {
    if (!isDragging) return;

    const { clientX } = event.touches[0];
    const delta = clientX - dragOrigin.current;

    const isAtFirstSlide = state.index === 0;
    const isAtLastSlide = state.index >= state.slides.length - 1;

    // Always prevent default to avoid scrolling conflicts
    event.preventDefault();

    if (!isInfinite && isAtLastSlide && delta < 0) {
      setDragDelta(0);
      return;
    }

    if (isAtFirstSlide && delta > 0) {
      setDragDelta(delta * 0.1);
      return;
    }

    // Always update delta for smooth dragging, remove threshold check during drag
    setDragDelta(delta);
  };

  const handleOnTouchEnd = () => {
    const isTouchEvent = true;
    setIsDragging(false);
    if (dragDelta === 0) return;
    handleDetermineNextSlideAfterDrag(dragDelta, isTouchEvent);

    isClickBlocked.current = true;
    setTimeout(() => { isClickBlocked.current = false; }, 0);
    onDragReset();
  };

  useEffect(() => {
    setContainerWidth(containerRef.current?.offsetWidth);
  }, [windowWidth, containerRef]);

  useEffect(() => {
    if (currentIndex !== undefined && currentIndex !== prevCurrentIndex.current) {
      const maxIndex = slides.length - 1;
      const validIndex = Math.max(0, Math.min(currentIndex, maxIndex));

      if (validIndex !== state.index) {
        isExternalUpdate.current = true;
        const correctedIndex = handleOnSlideSelect(validIndex);

        if (correctedIndex !== currentIndex && onSlideChange) {
          onSlideChange(slideRefs.current, correctedIndex, focusSource);
        }
      }
      prevCurrentIndex.current = validIndex;
    }
  }, [currentIndex, state.index, handleOnSlideSelect, slides.length, onSlideChange, focusSource]);

  useEffect(() => {
    const hasIndexChanged = prevStateIndex.current !== state.index;

    if (!hasIndexChanged) return;

    prevStateIndex.current = state.index;

    if (isExternalUpdate.current) {
      isExternalUpdate.current = false;
      if (onSlideChange && currentIndex !== undefined) {
        onSlideChange(slideRefs.current, currentIndex, focusSource);
      }
    } else if (onSlideChange) {
      onSlideChange(slideRefs.current, state.index, focusSource);
    }
  }, [state.index, onSlideChange, currentIndex, focusSource]);

  // Calculate translation for smooth dragging
  let translation;
  if (!isDragging) {
    translation = state.offset;
  } else {
    const isTouchDevice = 'ontouchstart' in window;

    if (isTouchDevice) {
      const isAtFirstSlide = state.index === 0;
      const isAtLastSlide = state.index >= state.slides.length - 1;

      if (isAtFirstSlide && dragDelta > 0) {
        translation = state.offset + (dragDelta * 0.2);
      } else if (isAtLastSlide && dragDelta < 0) {
        translation = state.offset + (dragDelta * 0.2);
      } else {
        translation = setLimitsOnOffset(
          state.offset + dragDelta,
          containerRef.current?.offsetWidth,
          trackRef.current?.offsetWidth,
        );
      }
    } else {
      translation = setLimitsOnOffset(
        state.offset + dragDelta,
        containerRef.current?.offsetWidth,
        trackRef.current?.offsetWidth,
      );
    }
  }

  // Prepare slide interaction handlers
  const slideInteractionHandlers = {
    onMouseDown: handleOnMouseDown,
    onMouseUp: handleOnMouseUp,
    onTouchEnd: handleOnTouchEnd,
    onTouchStart: handleOnTouchStart,
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <section
      aria-roledescription={description}
      onMouseLeave={handleOnMouseLeave}
      className="slider"
    >
      <SliderControls
        isScrollAllowed={isScrollAllowed}
        showControls={showControls}
        sliderControlOffset={sliderControlOffset}
        previousLabel={previousLabel}
        nextLabel={nextLabel}
        onSlideLeft={handleOnSlideLeft}
        onSlideRight={handleOnSlideRight}
        isInfinite={isInfinite}
        state={state}
      />
      <div
        className={`slider-container ${!isScrollAllowed ? 'centered' : ''}`}
        ref={containerRef}
      >
        <div
          aria-live="off"
          role="listbox"
          aria-label={label}
          tabIndex={0}
          className={`track ${!isDragging ? '' : 'draggable'}`}
          data-testid="slider-track"
          onMouseMove={handleOnMouseMove}
          onTouchMove={handleOnTouchMove}
          onFocus={(e) => onFocus(slideRefs.current, state.index, focusSource, e)}
          ref={trackRef}
          style={{
            transform: `translateX(${translation}px)`,
            touchAction: 'none',
          }}
        >
          <SlideList
            slides={slides}
            state={state}
            slideRefs={slideRefs}
            isScrollAllowed={isScrollAllowed}
            slidesToShow={slidesToShow}
            containerWidth={containerWidth}
            minimumSlideWidth={minimumSlideWidth}
            onSlideInteraction={slideInteractionHandlers}
          />
        </div>
      </div>
    </section>
  );
}

Slider.propTypes = sliderPropTypes;
